import { createContext, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// 서랍 안에서 연 팝오버도 자기 안에서 Tab을 순환시킨다. 포털이어도 React 문맥은 이어진다.
export const SyllabusModalContext = createContext(false);
const FOCUSABLE = 'input:not([disabled]),textarea:not([disabled]),select:not([disabled]),button:not([disabled]),[href],[tabindex]:not([tabindex="-1"])';

/** 기존 흰 진도 패널을 그대로 사용하는 중간 폭 서랍. 앱 전체 배경과 키보드 초점을 함께 보호한다. */
export default function SyllabusTreeDrawer({ openerRef, onClose, children }) {
  const paneRef = useRef(null);
  const restoreTimer = useRef(null);

  useLayoutEffect(() => {
    clearTimeout(restoreTimer.current);
    const opener = openerRef.current || document.activeElement;
    const app = document.getElementById('root');
    const priorInert = app?.getAttribute('inert');
    const priorHidden = app?.getAttribute('aria-hidden');
    const pane = paneRef.current;
    (pane.querySelector(FOCUSABLE) || pane).focus();
    app?.setAttribute('inert', '');
    app?.setAttribute('aria-hidden', 'true');
    return () => {
      if (priorInert == null) app?.removeAttribute('inert'); else app?.setAttribute('inert', priorInert);
      if (priorHidden == null) app?.removeAttribute('aria-hidden'); else app?.setAttribute('aria-hidden', priorHidden);
      restoreTimer.current = setTimeout(() => {
        if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
      }, 0);
    };
  }, [openerRef]);

  const onKeyDown = (e) => {
    if (e.defaultPrevented || e.nativeEvent?.isComposing) return;
    // 자식 팝오버의 Escape/Tab은 그 판에서 처리한다.
    if (!paneRef.current?.contains(e.target)) return;
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab') return;
    const pane = paneRef.current;
    const list = [...pane.querySelectorAll(FOCUSABLE)].filter((n) => (
      !n.closest('[hidden],[inert],[aria-hidden="true"]') && getComputedStyle(n).display !== 'none'
    ));
    const first = list[0];
    const last = list[list.length - 1];
    if (!first) { e.preventDefault(); pane.focus(); }
    else if (e.shiftKey && (document.activeElement === first || document.activeElement === pane)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return createPortal(
    <SyllabusModalContext.Provider value>
      <div className="fixed inset-0 flex bg-black/10 p-4" style={{ zIndex: 9999971 }}>
        <div
          ref={paneRef}
          role="dialog"
          aria-modal="true"
          aria-label="진도 목록"
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className="h-full w-[min(360px,90vw)] shrink-0 rounded-3xl outline-none"
        >
          {children}
        </div>
        <button type="button" tabIndex={-1} aria-label="진도 목록 닫기" onClick={onClose} className="flex-1" />
      </div>
    </SyllabusModalContext.Provider>,
    document.body,
  );
}
